 //External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import CustomerPondModel from './customerPond.model';
import FarmSiteModel from './farmSite.model';

/**
 * @service CustomerPondService
 * @description Service for managing customer pond data operations.
 * Provides HTTP methods for retrieving, filtering, and summarizing pond data associated with farm sites.
 * Handles operations like fetching active/inactive ponds, stocking density summaries, and pond assignments.
 * Extends base Service class with generic type CustomerPondModel.
 *
 * @feature 1. Fetch pond details by farm site and section
 * @feature 2. Get active/inactive pond counts and summaries
 * @feature 3. Calculate stocking density metrics
 * @feature 4. Assign persons (investors) to ponds
 * @feature 5. Cancel ongoing HTTP requests to prevent memory leaks
 *
 * @architecture
 * Extends base Service<CustomerPondModel> providing REST API integration with configurable base URL
 * and service endpoint. Implements request tracking for cleanup and cancellation. Uses HttpClient
 * for HTTP operations with standard error handling.
 *
 * @service-endpoint /aqu/customerpond/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerPondService extends Service<CustomerPondModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient
   *
   * Initializes service by calling parent Service constructor with baseUrl
   * configuration and service endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/customerpond/');
  }

  /**
   * @method getAllPondDetailsByFarmSiteId
   * @async
   * @description Fetch all pond details for a specific farm site (excluding BH ponds)
   *
   * @param {string} farmSiteId - ID of the farm site
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of CustomerPondModel objects
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getAllCustomerPonds() {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=10000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error.error.message);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllPondDetailsByFarmSiteId
   * @async
   * @description Fetch all pond details for a specific farm site (excluding BH ponds)
   *
   * @param {string} farmSiteId - ID of the farm site
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of CustomerPondModel objects
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getAllPondDetailsByFarmSiteId(farmSiteId: string) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}farmSiteId/${farmSiteId}/isBH/0`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error.error.message);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondsByFarmSiteId
   * @async
   * @description Fetch detailed pond information for multiple farm sites via POST
   *
   * @param {any} farmsitesId - Array or single ID of farm sites
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of CustomerPondModel objects
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getPondsByFarmSiteId(farmsitesId: any) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}pondsDetail/farmSiteId`, {
        farmSite: farmsitesId
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getCurrentDatePondsByFarmSiteId
   * @async
   * @description Fetch ponds for specific farm sites filtered by current date context
   *
   * @param {FarmSiteModel[]} farmSiteIds - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of CustomerPondModel objects
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getCurrentDatePondsByFarmSiteId(farmSiteIds: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}date`, {
        farmSite: farmSiteIds,
        isBH: 0
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllPondDetailsByFarmsiteId
   * @async
   * @description Fetch unassigned ponds for a specific farm site
   *
   * @param {string} farmSiteId - ID of the farm site
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of unassigned CustomerPondModel objects
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getAllPondDetailsByFarmsiteId(farmSiteId: string) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}unAssigned/ponds/farmSiteId/${farmSiteId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getActivePondsCount
   * @async
   * @description Get count of active ponds and total stocking area for farm sites
   *
   * @param {FarmSiteModel[]} farmSiteIds - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array containing count data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getActivePondsCount(farmSiteIds: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}totalPondStockingArea/count/farmSite`, {
        farmSite: farmSiteIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getInactivePondsCount
   * @async
   * @description Get count of inactive ponds and inactive stocking area for farm sites
   *
   * @param {FarmSiteModel[]} farmSiteIds - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array containing count data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getInactivePondsCount(farmSiteIds: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}inActivePondStockingArea/count/farmSite`, {
        farmSite: farmSiteIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondsByFarmsites
   * @async
   * @description Fetch ponds associated with multiple farm sites
   *
   * @param {FarmSiteModel[]} farmSiteIds - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of CustomerPondModel objects
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getPondsByFarmsites(farmSiteIds: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}farmSites`, {
        farmSiteId: farmSiteIds
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getStockingDensitySummary
   * @async
   * @description Get stocking density summary for farm sites
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to stocking density summary data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getStockingDensitySummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}stockingDensity/summary/farmSite`, {
        farmSite: farmsites
       }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getStockingDensityLocationSummary
   * @async
   * @description Get stocking density summary grouped by location
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to location-based stocking density data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getStockingDensityLocationSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}stockingDensity/location/farmSite`, {
        farmSite: farmsites
       }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getStockingDensityDetails
   * @async
   * @description Get detailed stocking density metrics for farm sites
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to detailed stocking density metrics
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getStockingDensityDetails(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}stockingDensity/metrics/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getActivePondSummary
   * @async
   * @description Get summary of active ponds for farm sites
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to active pond summary data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getActivePondSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}activePond/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getInActivePondSummary
   * @async
   * @description Get summary of inactive ponds for farm sites
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to inactive pond summary data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getInActivePondSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}inActivePond/summary/farmSite`, {
         farmSite: farmsites
         }, this.httpOptions).subscribe(response => {
          resolve(response);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondSizeSummary
   * @async
   * @description Get summary of pond sizes for farm sites
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to pond size summary data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getPondSizeSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}pondSize/summary/farmSite`, {
         farmSite: farmsites
        }, this.httpOptions).subscribe(response => {
          resolve(response);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getActivePondLocation
   * @async
   * @description Get active pond locations for farm sites
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to active pond location data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getActivePondLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}activityPond/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getInActivePondLocation
   * @async
   * @description Get inactive pond locations for farm sites
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to inactive pond location data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getInActivePondLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}inActivityPond/location/farmSite`, {
         farmSite: farmsites
        }, this.httpOptions).subscribe(response => {
          resolve(response);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondSizeLocation
   * @async
   * @description Get pond size distribution by location
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to pond size location data
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getPondSizeLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}pondSize/location/farmSite`, {
         farmSite: farmsites
         }, this.httpOptions).subscribe(response => {
          resolve(response);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getActivePondDetails
   * @async
   * @description Get detailed metrics for active ponds
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to active pond metrics
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getActivePondDetails(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}activePond/metrics/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getInActivePondDetails
   * @async
   * @description Get detailed metrics for inactive ponds
   *
   * @param {FarmSiteModel[]} farmsites - Array of FarmSiteModel objects
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to inactive pond metrics
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getInActivePondDetails(farmsites: FarmSiteModel[]) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}inActivePond/metrics/summary/farmSite`, {
        farmSite: farmsites
       }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getFeedProviderSumaryByFarmSite
   * @async
   * @description Get summary of feed providers for a farm site
   *
   * @param {string} feedProviderId - ID of the feed provider
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to feed provider summary
   * @side-effect Creates HTTP POST request and stores subscription in ongoingRequests
   */
  public getFeedProviderSumaryByFarmSite(feedProviderId: string) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}feedProvider`, {
        feedProviderId: feedProviderId
       }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method assignPersonToPond
   * @async
   * @description Assign a person (investor/manager) to specific ponds
   *
   * @param {string} personId - ID of the person to assign
   * @param {CustomerPondModel[]} pondIds - Array of pond objects to assign
   * @returns {Promise<CustomerPondModel>} Promise resolving to updated pond model
   * @side-effect Creates HTTP PUT request and stores subscription in ongoingRequests
   */
  public assignPersonToPond(personId: string, pondIds: CustomerPondModel[]) {
    return new Promise<CustomerPondModel>((resolve, reject) => {
      const request = this.httpClient.put<CustomerPondModel>(`${this.baseUrl}${this.serviceUrl}updateFarmInvestor`, {
        farmInvestorId: personId,
         ids: pondIds
        }, this.httpOptions).subscribe(response => {
          resolve(response);
        }, (error: HttpErrorResponse) => {
          reject(error);
        });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllPondsByFarmSiteId
   * @async
   * @description Fetch all ponds for a farm site (excluding BH ponds)
   *
   * @param {string} farmSiteId - ID of the farm site
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of CustomerPondModel objects
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getAllPondsByFarmSiteId(farmSiteId: string) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}farmSiteId/${farmSiteId}/isBH/0`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllPondsBySectionId
   * @async
   * @description Fetch all ponds for a specific section with feed logs
   *
   * @param {number | string} sectionId - ID of the pond section
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of CustomerPondModel objects
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getAllPondsBySectionId(sectionId: number | string) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}feedLogs/sectionId/${sectionId}/isBH/0/farmInvestorId/-1`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPondsBySectionId
   * @async
   * @description Fetch basic pond details for a specific section
   *
   * @param {string} sectionId - ID of the pond section
   * @returns {Promise<Array<CustomerPondModel>>} Promise resolving to array of CustomerPondModel objects
   * @side-effect Creates HTTP GET request and stores subscription in ongoingRequests
   */
  public getPondsBySectionId(sectionId: string) {
    return new Promise<Array<CustomerPondModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<CustomerPondModel>>(`${this.baseUrl}${this.serviceUrl}sectionId/${sectionId}`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks
   *
   * Iterates through ongoingRequests array and unsubscribes from each active
   * subscription. Clears the requests array after unsubscribing from all.
   *
   * @execution-flow
   * 1. Iterate through each subscription in ongoingRequests array
   * 2. Check if subscription has unsubscribe function
   * 3. Call unsubscribe() on each active subscription
   * 4. Clear ongoingRequests array
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests
   * - Clears ongoingRequests array
   *
   * @usage Called during component ngOnDestroy to cleanup subscriptions
   * @example
   * ngOnDestroy() {
   *   this.customerPondService.cancelOnGoingHttpRequests();
   * }
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
